// ============================================================================
// PackDashboard.jsx
//
// Unified React renderer for all automation/AI packs.
// Replaces: render_JiraAutomation, JMWEDashboard, JWTDashboard,
//           scriptrunnerDashboard, RovoDashboard (grid view)
//
// Each engine passes its own config telling the renderer:
//  - how to extract rules from the JSON
//  - what fields to show per rule
//  - what the "implementation" artifact is (code, JSON, expressions)
//  - what the technical requirements section looks like
// ============================================================================

import React, { useState } from 'react';
import { Check, ChevronDown, ChevronRight, Copy, Download } from 'lucide-react';

// ── Shared styles ─────────────────────────────────────────────────────────────
const C = {
  navy:    '#172B4D',
  blue:    '#0052CC',
  subtle:  '#42526E',
  muted:   '#6B778C',
  border:  '#DFE1E6',
  bg:      '#F4F5F7',
  bgCard:  '#FFFFFF',
  bgAlt:   '#FAFBFC',
  code:    '#1e2432',
  codeText:'#e8eeff',
  green:   '#006644',
  greenBg: '#E3FCEF',
  red:     '#BF2600',
  redBg:   '#FFEBE6',
  amber:   '#974F0C',
  amberBg: '#FFF0B3',
};

const priority_color = (p) => {
  if (!p) return { bg: C.bgAlt, text: C.muted, border: C.border };
  const lo = String(p).toLowerCase();
  if (lo === 'high' || lo === 'critical')
    return { bg: C.redBg,   text: C.red,   border: '#FF8F73' };
  if (lo === 'medium')
    return { bg: C.amberBg, text: C.amber, border: '#FFE380' };
  return { bg: '#DEEBFF', text: '#0747A6', border: '#B3D4FF' };
};

const effort_color = (e) => {
  const lo = String(e || '').toLowerCase();
  if (lo === 'low')    return { bg: C.greenBg, text: C.green, border: '#57D9A3' };
  if (lo === 'medium') return { bg: C.amberBg, text: C.amber, border: '#FFE380' };
  return { bg: C.redBg, text: C.red, border: '#FF8F73' };
};

// ── Sub-components ────────────────────────────────────────────────────────────

const Pill = ({ text, colors, style = {} }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    padding: '2px 9px', borderRadius: 999,
    fontSize: 11, fontWeight: 700,
    background: colors.bg, color: colors.text,
    border: `1px solid ${colors.border}`,
    ...style,
  }}>{text}</span>
);

const CopyBtn = ({ text, label = 'Copy' }) => {
  const [done, setDone] = useState(false);
  const copy = () => {
    const t = typeof text === 'object' ? JSON.stringify(text, null, 2) : String(text || '');
    if (navigator.clipboard) {
      navigator.clipboard.writeText(t).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); });
    } else {
      const ta = document.createElement('textarea');
      ta.value = t; ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy');
      document.body.removeChild(ta);
      setDone(true); setTimeout(() => setDone(false), 1500);
    }
  };
  return (
    <button onClick={copy} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: done ? '#00875A' : C.blue, color: '#fff',
      border: 'none', padding: '3px 10px', borderRadius: 4,
      fontSize: 11, fontWeight: 700, cursor: 'pointer',
      transition: 'background 0.2s', fontFamily: 'inherit',
    }}>
      {done ? <Check size={11} /> : <Copy size={11} />}
      {done ? 'Copied!' : label}
    </button>
  );
};

const CodeBlock = ({ label, lang, text, downloadName }) => {
  if (!text) return null;
  const str = typeof text === 'object' ? JSON.stringify(text, null, 2) : String(text);
  const handleDownload = () => {
    const ext = lang?.toLowerCase().includes('json') ? 'json' : 'txt';
    const blob = new Blob([str], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${downloadName || 'export'}.${ext}`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 12px', background: C.code, borderRadius: '6px 6px 0 0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: '#8892b0' }}>{label}</span>
          {lang && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: '#0052CC22', color: '#4C8FEB' }}>{lang}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <CopyBtn text={str} />
          {downloadName && (
            <button onClick={handleDownload} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: '#00875A', color: '#fff',
              border: 'none', padding: '3px 10px', borderRadius: 4,
              fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              <Download size={11} /> Download
            </button>
          )}
        </div>
      </div>
      <pre style={{
        margin: 0, background: C.code, padding: '14px 12px',
        borderRadius: '0 0 6px 6px',
        fontFamily: "Monaco, Menlo, 'Ubuntu Mono', Consolas, monospace",
        fontSize: 12, color: C.codeText, lineHeight: 1.65,
        overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        maxHeight: 360, overflowY: 'auto',
      }}>{str}</pre>
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <div style={{
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '.08em', color: C.muted, marginBottom: 6,
  }}>{children}</div>
);

const Cell = ({ label, children, span2 = false }) => (
  <div style={{
    padding: '12px 16px',
    borderRight: span2 ? 'none' : `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    gridColumn: span2 ? 'span 2' : undefined,
  }}>
    <SectionLabel>{label}</SectionLabel>
    <div style={{ fontSize: 13, color: C.subtle, lineHeight: 1.65 }}>{children}</div>
  </div>
);

// ── Collapsible section ───────────────────────────────────────────────────────
const Section = ({ title, accent = C.blue, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginTop: 0, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
      <button onClick={() => setOpen(!open)} style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', background: `${accent}0D`, border: 'none', cursor: 'pointer',
        borderBottom: open ? `1px solid ${C.border}` : 'none',
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '.06em' }}>{title}</span>
        {open ? <ChevronDown size={14} color={accent} /> : <ChevronRight size={14} color={accent} />}
      </button>
      {open && <div style={{ padding: '14px 16px' }}>{children}</div>}
    </div>
  );
};

// ── Requirements section (shared across all engines) ──────────────────────────
const RequirementsSection = ({ requirements }) => {
  if (!requirements?.length) return null;
  return (
    <Section title="Technical Requirements" accent="#974F0C" defaultOpen={true}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {requirements.map((req, i) => {
          const text = typeof req === 'string' ? req : (req.requirement || req.description || req.text || JSON.stringify(req));
          const type = typeof req === 'object' ? (req.type || req.category || '') : '';
          const typeColor = type.toLowerCase().includes('field') ? '#0052CC' :
                            type.toLowerCase().includes('permission') ? '#BF2600' :
                            type.toLowerCase().includes('workflow') ? '#00875A' : '#42526E';
          return (
            <div key={i} style={{
              display: 'flex', gap: 10, padding: '8px 12px',
              background: C.bgAlt, borderRadius: 6,
              border: `1px solid ${C.border}`,
              borderLeft: `4px solid ${typeColor}`,
            }}>
              <div style={{ flex: 1 }}>
                {type && <span style={{ fontSize: 10, fontWeight: 700, color: typeColor, textTransform: 'uppercase', letterSpacing: '.05em', marginRight: 8 }}>{type}</span>}
                <span style={{ fontSize: 13, color: C.subtle }}>{text}</span>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};


const formatJWTList = (items = [], prefix = '') =>
  (items || []).filter(Boolean).map((item) => prefix ? `${prefix}: ${item}` : item);

const summarizeJWTTransitionConfigs = (configs = []) => {
  return (configs || []).flatMap((cfg) => {
    const rows = [];
    const name = cfg?.transition_name || 'Transition';
    (cfg?.validators || []).forEach(v => rows.push(`${name} — validator: ${v}`));
    (cfg?.conditions || []).forEach(v => rows.push(`${name} — condition: ${v}`));
    (cfg?.post_functions || []).forEach(v => rows.push(`${name} — post-function: ${v}`));
    return rows;
  });
};

const summarizeJWTScheduledRules = (rules = []) => {
  return (rules || []).flatMap((rule) => {
    const headerBits = [rule?.name, rule?.cron_expression || rule?.schedule_description].filter(Boolean);
    const prefix = headerBits.join(' — ');
    const rows = [];
    if (rule?.jql_scope) rows.push(`${prefix} — JQL: ${rule.jql_scope}`.trim());
    (rule?.actions || []).forEach(action => rows.push(`${prefix} — action: ${action}`.trim()));
    return rows;
  });
};

// ── Engine-specific rule normalizers ──────────────────────────────────────────

// Normalise any engine's rule to a common shape
const normalizeJira = (rule, idx) => ({
  id: rule.id || `RULE-${idx + 1}`,
  name: rule.name || 'Unnamed Rule',
  purpose: rule.description || rule.references?.rationale || '',
  priority: rule.risk_level,
  effort: null,
  trigger: rule.triggers?.[0]?.type?.replace('jira.', '').replace(/_/g, ' ') || '',
  actions: rule.actions?.map(a => {
    if (typeof a === 'string') return a;
    if (!a || typeof a !== 'object') return String(a);
    const actionType = a.type?.replace('jira.', '').replace(/\./g, ' ').replace(/_/g, ' ') || 'unknown action';
    const configPreview = a.config ? `: ${JSON.stringify(a.config).slice(0, 80)}` : '';
    return actionType + configPreview;
  }) || [],

  conditions: rule.conditions?.map(c => c.config?.queryString || c.type) || [],
  evidence: rule.references?.related_transitions || [],
  implementation: rule.export?.a4j_cloud || null,
  implementationLabel: 'A4J Import JSON',
  implementationLang: 'Jira Automation JSON',
  downloadName: rule.id || `jira-rule-${idx + 1}`,
  requirements: rule.requirements || [],
  tags: rule.tags || [],
});

const normalizeScriptRunner = (rule, idx) => ({
  id: rule.rule_id || `SR-${idx + 1}`,
  name: rule.name || 'Unnamed Rule',
  purpose: rule.intent || '',
  priority: null,
  effort: null,
  trigger: rule.trigger?.type + (rule.trigger?.schedule ? ` (${rule.trigger.schedule})` : ''),
  actions: rule.actions || [],
  conditions: rule.when || [],
  evidence: rule.references?.transitions || [],
  implementation: rule.script?.code || null,
  implementationLabel: 'Script',
  implementationLang: rule.script?.language || 'groovy',
  downloadName: rule.rule_id || `sr-rule-${idx + 1}`,
  requirements: rule.risk_notes?.map(n => ({ requirement: n, type: 'Risk' })) || [],
  tags: [],
});

const normalizeJMWE = (rule, idx) => {
  const actions = (rule.jmwe_actions || []);
  return {
    id: rule.rule_id || `JMWE-${idx + 1}`,
    name: rule.name || 'Unnamed Rule',
    purpose: rule.rationale || '',
    priority: rule.priority,
    effort: null,
    trigger: rule.target_transitions?.map(t => `${t.from} → ${t.to}`).join(', ') || '',
    actions: actions.map(a => `${a.type}: ${a.name}`),
    conditions: rule.preconditions?.jql ? [`JQL: ${rule.preconditions.jql}`] : [],
    evidence: [],
    implementation: actions.length ? actions.map(a => ({
      type: a.type, name: a.name, config: a.config || {},
    })) : null,
    implementationLabel: 'JMWE Configuration',
    implementationLang: 'JMWE JSON',
    downloadName: rule.rule_id || `jmwe-rule-${idx + 1}`,
    requirements: (rule.risks || []).map(r => ({ requirement: r, type: 'Risk' })),
    tags: [],
  };
};

const normalizeJWT = (rule, idx) => {
  // JWT deliberately mirrors the JMWE/ScriptRunner rule-pack pattern.
  // The only substantive difference is the automation app name and the
  // action collection name: jwt_actions.
  if (Array.isArray(rule.jwt_actions)) {
    const actions = rule.jwt_actions || [];
    const targetTransitions = Array.isArray(rule.target_transitions)
      ? rule.target_transitions.map(t => typeof t === 'string' ? t : `${t.from} → ${t.to}`).join(', ')
      : '';
    return {
      id: rule.rule_id || `JWT-${idx + 1}`,
      name: rule.name || 'Unnamed Rule',
      purpose: rule.rationale || '',
      priority: rule.priority,
      effort: null,
      trigger: targetTransitions,
      actions: actions.map(a => `${a.type}: ${a.name}`),
      conditions: rule.preconditions?.jql ? [`JQL: ${rule.preconditions.jql}`] : (rule.preconditions?.notes ? [rule.preconditions.notes] : []),
      evidence: [
        ...(rule.references?.transitions || []),
        ...(rule.references?.variants || []),
        ...(rule.references?.evidence || []),
      ],
      implementation: actions.length ? actions.map(a => ({
        type: a.type, name: a.name, config: a.config || {},
      })) : null,
      implementationLabel: 'JWT Configuration',
      implementationLang: 'JWT JSON',
      downloadName: rule.rule_id || `jwt-rule-${idx + 1}`,
      requirements: (rule.risks || []).map(r => ({ requirement: r, type: 'Risk' })),
      tags: [],
    };
  }

  // Backwards compatibility for any saved JWT packs generated before the
  // rules[] alignment.
  const impl = rule.jwt_implementation || {};
  const appliesTo = rule.applies_to || {};
  const actions = [
    ...(impl.configuration_steps || []),
    ...summarizeJWTTransitionConfigs(impl.transition_configuration || []),
    ...summarizeJWTScheduledRules(impl.scheduled_rules || []),
  ].filter(Boolean);

  const conditions = [
    ...formatJWTList(impl.preconditions || [], 'Prerequisite'),
    ...((impl.transition_configuration || []).flatMap(cfg => (cfg?.validators || []).map(v => `${cfg.transition_name || 'Transition'} — validator: ${v}`))),
    ...((impl.transition_configuration || []).flatMap(cfg => (cfg?.conditions || []).map(v => `${cfg.transition_name || 'Transition'} — condition: ${v}`))),
    ...((impl.scheduled_rules || []).filter(r => r?.jql_scope).map(r => `${r.name || 'Scheduled rule'} — JQL: ${r.jql_scope}`)),
  ].filter(Boolean);

  return {
    id: rule.id || `JWT-${idx + 1}`,
    name: rule.title || 'Unnamed Rule',
    purpose: [rule.problem_statement, rule.goal].filter(Boolean).join(' '),
    priority: rule.priority || rule.risk_and_considerations?.risk_level || null,
    effort: null,
    trigger:
      (impl.transition_configuration || []).map(cfg => cfg?.transition_name).filter(Boolean).join(', ')
      || (impl.scheduled_rules || []).map(r => r?.name || r?.cron_expression || r?.schedule_description).filter(Boolean).join(', ')
      || '',
    actions,
    conditions,
    evidence: [
      ...(rule.evidence_signals || []),
      ...(rule.related_transitions || []),
      ...(rule.related_variants || []),
    ].filter(Boolean),
    implementation: {
      summary: impl.summary || '',
      transition_configuration: impl.transition_configuration || [],
      scheduled_rules: impl.scheduled_rules || [],
      expression_snippets: impl.expression_snippets || [],
    },
    implementationLabel: 'JWT Configuration',
    implementationLang: 'JWT JSON',
    downloadName: rule.id || `jwt-rule-${idx + 1}`,
    requirements: [
      ...formatJWTList(appliesTo.project_keys || [], 'Project'),
      ...formatJWTList(appliesTo.issue_types || [], 'Issue type'),
      ...formatJWTList(appliesTo.workflow_names || [], 'Workflow'),
      ...formatJWTList(rule.success_criteria || [], 'Success criteria'),
      ...formatJWTList(rule.risk_and_considerations?.risks || [], 'Risk'),
      ...formatJWTList(rule.risk_and_considerations?.rollout_recommendations || [], 'Rollout'),
    ].map((requirement) => ({ requirement, type: requirement.split(':')[0] })),
    tags: [],
  };
};

const normalizeServiceNow = (rule, idx) => ({
  id: rule.id || rule.rule_id || `SN-${idx + 1}`,
  name: rule.name || rule.title || 'Unnamed ServiceNow Automation',
  purpose: rule.purpose || rule.description || rule.rationale || '',
  priority: rule.priority,
  effort: rule.setup_effort || rule.effort,
  trigger: rule.trigger || rule.when || '',
  actions: rule.actions || rule.flow_steps || rule.implementation_steps || [],
  conditions: rule.conditions || rule.preconditions || [],
  evidence: rule.evidence || rule.evidence_points || rule.evidence_signals || [],
  implementation: rule.implementation || rule.configuration || rule.script || null,
  implementationLabel: rule.implementation_label || rule.configuration_label || 'ServiceNow Configuration',
  implementationLang: rule.implementation_lang || 'ServiceNow JSON',
  downloadName: rule.id || rule.rule_id || `servicenow-rule-${idx + 1}`,
  requirements: (rule.requirements || rule.prerequisites || []).map(p => typeof p === 'string' ? ({ requirement: p, type: 'Prerequisite' }) : p),
  tags: rule.capability ? [rule.capability] : [],
});

const normalizeRovo = (agent, idx) => ({
  id: agent.id || `ROVO-${idx + 1}`,
  name: agent.name || 'Unnamed Agent',
  purpose: agent.purpose || '',
  priority: agent.priority,
  effort: agent.setup_effort,
  trigger: agent.trigger || '',
  actions: agent.actions || [],
  conditions: agent.prerequisites || [],
  evidence: [agent.evidence_signal].filter(Boolean),
  implementation: agent.agent_instructions || null,
  implementationLabel: 'Agent Instructions',
  implementationLang: 'Paste into Rovo Agent',
  implementation2: agent.jira_automation_json || null,
  implementation2Label: 'Jira Automation Pairing',
  implementation2Lang: 'Importable JSON',
  downloadName: agent.id || `rovo-agent-${idx + 1}`,
  requirements: (agent.prerequisites || []).map(p => ({ requirement: p, type: 'Prerequisite' })),
  rovo_product: agent.rovo_product,
  requires_studio: agent.requires_studio,
  tags: [],
});

const ROVO_COLORS = {
  'Rovo Agents': { bg: '#E6F1FB', text: '#0052CC', border: '#85B7EB', dot: '#0052CC' },
  'Rovo Search': { bg: '#EAE6FF', text: '#5243AA', border: '#998DD9', dot: '#6554C0' },
  'Rovo Chat':   { bg: '#E3FCEF', text: '#006644', border: '#57D9A3', dot: '#00875A' },
  'Rovo Studio': { bg: '#FFF0B3', text: '#974F0C', border: '#FFE380', dot: '#FF8B00' },
};

// ── Rule card ─────────────────────────────────────────────────────────────────
const RuleCard = ({ rule, accentColor, index }) => {
  const pc = priority_color(rule.priority);
  const ec = effort_color(rule.effort);
  const rovo = rule.rovo_product ? (ROVO_COLORS[rule.rovo_product] || ROVO_COLORS['Rovo Agents']) : null;

  return (
    <div style={{
      background: C.bgCard, border: `1px solid ${C.border}`,
      borderRadius: 10, marginBottom: 10, overflow: 'hidden',
    }}>
      {/* Rule header */}
      <div style={{
        padding: '14px 16px',
        borderBottom: `1px solid ${C.border}`,
        background: `linear-gradient(135deg, ${accentColor}08 0%, transparent 100%)`,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <span style={{
                fontSize: 10, fontWeight: 800, color: accentColor, textTransform: 'uppercase',
                letterSpacing: '.1em', background: `${accentColor}12`,
                padding: '2px 8px', borderRadius: 4,
              }}>
                {rule.id}
              </span>
              {rule.priority && <Pill text={rule.priority} colors={pc} />}
              {rule.effort && <Pill text={`Effort: ${rule.effort}`} colors={ec} />}
              {rule.altEngines?.length > 0 && (
                <Pill
                  text={`Same fix in: ${rule.altEngines.join(', ')} — deploy one`}
                  colors={{ bg: '#EAE6FF', text: '#403294', border: '#C0B6F2' }}
                />
              )}
              {rovo && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '2px 9px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                  background: rovo.bg, color: rovo.text, border: `1px solid ${rovo.border}`,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: rovo.dot }} />
                  {rule.rovo_product}
                </span>
              )}
              {rule.requires_studio && (
                <Pill text="★ Rovo Studio" colors={{ bg: '#FFF0B3', text: '#974F0C', border: '#FFE380' }} />
              )}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, lineHeight: 1.3 }}>{rule.name}</div>
            {rule.purpose && <div style={{ fontSize: 13, color: C.subtle, marginTop: 4, lineHeight: 1.5 }}>{rule.purpose}</div>}
          </div>
        </div>
      </div>

      {/* Compact trigger / conditions / actions row */}
      {(rule.trigger || rule.conditions?.length > 0 || rule.actions?.length > 0) && (
        <div style={{
          display: 'flex', gap: 0,
          borderTop: `1px solid ${C.border}`,
          fontSize: 12,
        }}>
          {rule.trigger && (
            <div style={{ flex: '0 0 auto', minWidth: 180, maxWidth: 260, padding: '10px 14px', borderRight: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: C.muted, marginBottom: 4 }}>Trigger</div>
              <div style={{ color: C.subtle, lineHeight: 1.4 }}>{rule.trigger}</div>
            </div>
          )}
          {rule.conditions?.length > 0 && (
            <div style={{ flex: 1, padding: '10px 14px', borderRight: rule.actions?.length > 0 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: C.muted, marginBottom: 4 }}>
                {rule.rovo_product ? 'Prerequisites' : 'Conditions'}
              </div>
              <div style={{ color: C.subtle, lineHeight: 1.5 }}>
                {rule.conditions.map((c, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: i < rule.conditions.length - 1 ? 3 : 0 }}>
                    <span style={{ color: C.muted, flexShrink: 0, marginTop: 1 }}>·</span>
                    <span>{c}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {rule.actions?.length > 0 && (
            <div style={{ flex: 1, padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: C.muted, marginBottom: 4 }}>Actions</div>
              <div style={{ color: C.subtle, lineHeight: 1.5 }}>
                {rule.actions.map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: i < rule.actions.length - 1 ? 3 : 0 }}>
                    <span style={{ color: C.muted, flexShrink: 0, marginTop: 1 }}>·</span>
                    <span>{a}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Implementation */}
      {rule.isExpressionList && Array.isArray(rule.implementation) ? (
        <div style={{ padding: '0 16px 12px' }}>
          <Section title="JWT Expressions" accent={accentColor} defaultOpen={false}>
            {rule.implementation.map((expr, i) => (
              <div key={i} style={{ marginBottom: i < rule.implementation.length - 1 ? 12 : 0 }}>
                {expr.label && <div style={{ fontSize: 12, fontWeight: 600, color: C.navy, marginBottom: 4 }}>{expr.label}</div>}
                <CodeBlock
                  label="Expression"
                  lang="JWT"
                  text={expr.expression || expr}
                  downloadName={`${rule.downloadName}-expr-${i + 1}`}
                />
              </div>
            ))}
          </Section>
        </div>
      ) : rule.implementation ? (
        <div style={{ padding: '0 16px 12px' }}>
          <Section title={rule.implementationLabel || 'Implementation'} accent={accentColor} defaultOpen={false}>
            <CodeBlock
              label={rule.implementationLabel || 'Code'}
              lang={rule.implementationLang}
              text={rule.implementation}
              downloadName={rule.downloadName}
            />
          </Section>
        </div>
      ) : null}

      {/* Second implementation artifact (Rovo: JA pairing) */}
      {rule.implementation2 && (
        <div style={{ padding: '0 16px 12px' }}>
          <Section title={rule.implementation2Label || 'Automation Pairing'} accent="#0052CC" defaultOpen={false}>
            <CodeBlock
              label={rule.implementation2Label}
              lang={rule.implementation2Lang}
              text={rule.implementation2}
              downloadName={`${rule.downloadName}-ja-pairing`}
            />
          </Section>
        </div>
      )}
    </div>
  );
};

// ── Engine configs ────────────────────────────────────────────────────────────
const ENGINE_CONFIG = {
  jira: {
    accent: '#0052CC',
    label: 'Jira Automation (A4J)',
    rulesKey: 'rules',
    normalize: normalizeJira,
    packRequirements: (data) => [
      { requirement: 'Jira Cloud with Automation for Jira enabled', type: 'Platform' },
      { requirement: 'Project Admin permission to import automation rules', type: 'Permission' },
      { requirement: `Import via: Project Settings → Automation → Import rule`, type: 'How to import' },
      ...(data?.source_context?.project_keys?.map(k => ({ requirement: `Project key: ${k}`, type: 'Scope' })) || []),
    ],
  },
  scriptrunner: {
    accent: '#00875A',
    label: 'ScriptRunner',
    rulesKey: 'rules',
    normalize: normalizeScriptRunner,
    packRequirements: (data) => [
      { requirement: 'ScriptRunner for Jira Cloud installed', type: 'App' },
      { requirement: 'Script Runner Admin permission', type: 'Permission' },
      { requirement: 'Scripts are Groovy using the built-in Unirest-style REST helpers (get/post/put) or HAPI', type: 'Language' },
      { requirement: 'Deploy via ScriptRunner → Script Console, Script Listeners or Scheduled Jobs', type: 'How to deploy' },
    ],
  },
  jmwe: {
    accent: '#FF8B00',
    label: 'JMWE',
    rulesKey: 'rules',
    normalize: normalizeJMWE,
    packRequirements: (data) => [
      { requirement: 'Jira Misc Workflow Extensions (JMWE) for Cloud installed', type: 'App' },
      { requirement: 'Workflow Admin permission', type: 'Permission' },
      { requirement: 'Configure via Workflow editor → Transition → Post-functions / Validators', type: 'How to deploy' },
      { requirement: 'Nunjucks template syntax: {{issue.summary}}, {{now | date: "yyyy-MM-dd"}}', type: 'Syntax' },
    ],
  },
  jwt: {
    accent: '#6554C0',
    label: 'Jira Workflow Toolbox',
    rulesKey: data => Array.isArray(data?.rules) ? 'rules' : 'automation_opportunities',
    normalize: normalizeJWT,
    packRequirements: (data) => [
      { requirement: 'Jira Workflow Toolbox (JWT) for Cloud installed', type: 'App' },
      { requirement: 'Workflow Admin permission', type: 'Permission' },
      { requirement: 'Configure via Workflow editor → Transition → JWT validators/conditions/post-functions', type: 'How to deploy' },
      { requirement: 'JWT expression syntax: ${issue.status.name}, ${now}', type: 'Syntax' },
    ],
  },
  servicenow: {
    accent: '#0A4A57',
    label: 'ServiceNow Automation Pack',
    rulesKey: 'rules',
    normalize: normalizeServiceNow,
    packRequirements: (data) => [
      ...(Array.isArray(data?.technical_requirements) && data.technical_requirements.length
        ? data.technical_requirements.map(r => typeof r === 'string' ? { requirement: r, type: 'Requirement' } : r)
        : [
            { requirement: 'ServiceNow scoped application / admin access', type: 'Platform' },
            { requirement: 'Flow Designer, Business Rules, Assignment Rules, and SLA configuration permissions as required', type: 'Permission' },
          ]),
      { requirement: 'Validate in sub-production before promoting through update sets or application repository', type: 'Deployment' },
      ...(data?.readiness ? [{ requirement: `Automation Readiness: ${data.readiness} — ${data.readiness_rationale || ''}`, type: 'Readiness' }] : []),
    ],
  },
  rovo: {
    accent: '#0052CC',
    label: 'Rovo Agent Pack',
    rulesKey: 'agents',
    normalize: normalizeRovo,
    packRequirements: (data) => [
      { requirement: 'Atlassian Rovo enabled for this Jira Cloud site', type: 'Platform' },
      { requirement: 'Rovo Agent builder access (Rovo Admin)', type: 'Permission' },
      ...(data?.rovo_readiness ? [{ requirement: `Rovo Readiness: ${data.rovo_readiness} — ${data.rovo_readiness_rationale || ''}`, type: 'Readiness' }] : []),
      { requirement: 'Rovo Studio required for agents marked ★ Rovo Studio', type: 'Premium' },
      { requirement: 'Jira Automation pairing rules deployed separately via A4J', type: 'Dependency' },
    ],
  },
};

// ── Pack summary bar ──────────────────────────────────────────────────────────
const PackSummary = ({ data, engine, ruleCount, accentColor }) => {
  const cfg = ENGINE_CONFIG[engine] || {};
  const reqs = cfg.packRequirements ? cfg.packRequirements(data) : [];
  const [reqsOpen, setReqsOpen] = useState(false);

  return (
    <div style={{ marginBottom: 12 }}>
      {/* Global technical requirements — collapsible */}
      <div style={{
        background: C.bgCard, border: `1px solid ${C.border}`,
        borderRadius: 10, marginBottom: 10,
        borderLeft: `4px solid #974F0C`,
        overflow: 'hidden',
      }}>
        {/* Clickable header */}
        <button
          onClick={() => setReqsOpen(o => !o)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '13px 18px', background: 'transparent', border: 'none',
            cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: C.navy }}>
            <span style={{ fontSize: 16 }}>⚙</span>
            Technical Requirements to Implement This Pack
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
              background: '#97440C18', color: '#974F0C', border: '1px solid #974F0C33',
            }}>{reqs.length}</span>
          </div>
          <span style={{
            fontSize: 14, color: C.muted, transition: 'transform 0.2s',
            transform: reqsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block',
          }}>▼</span>
        </button>

        {/* Collapsible body */}
        {reqsOpen && (
          <div style={{ padding: '0 18px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {reqs.map((req, i) => {
              const text = typeof req === 'string' ? req : req.requirement;
              const type = typeof req === 'object' ? req.type : '';
              const typeColor = type === 'Permission' ? '#BF2600' :
                                type === 'Platform' || type === 'App' ? '#0052CC' :
                                type === 'Premium' || type === 'Readiness' ? '#974F0C' :
                                type === 'Dependency' ? '#6554C0' : '#42526E';
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '7px 10px', background: C.bgAlt, borderRadius: 6,
                  border: `1px solid ${C.border}`,
                }}>
                  {type && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                      background: `${typeColor}18`, color: typeColor,
                      border: `1px solid ${typeColor}33`, whiteSpace: 'nowrap', flexShrink: 0,
                    }}>{type}</span>
                  )}
                  <span style={{ fontSize: 13, color: C.subtle }}>{text}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main export ───────────────────────────────────────────────────────────────
const PackDashboard = ({ data, engine, crossPackThemes = null }) => {
  if (!data) return <div style={{ padding: 20, color: C.muted }}>No data available.</div>;

  const cfg = ENGINE_CONFIG[engine] || ENGINE_CONFIG.jira;
  const rulesKey = typeof cfg.rulesKey === 'function' ? cfg.rulesKey(data) : cfg.rulesKey;
  const rawRules = Array.isArray(data[rulesKey]) ? data[rulesKey] : [];
  // Which other packs implement the same fix (matched on fix_theme)
  const OWN_LABEL = { jira: 'Jira Automation', scriptrunner: 'ScriptRunner', jmwe: 'JMWE', jwt: 'JWT' }[engine] || null;
  const rules = rawRules.map((r, i) => {
    const norm = cfg.normalize(r, i);
    const fixTheme = r?.fix_theme || null;
    const altEngines = (fixTheme && crossPackThemes && OWN_LABEL)
      ? (crossPackThemes[fixTheme] || []).filter(l => l !== OWN_LABEL)
      : [];
    return { ...norm, fixTheme, altEngines };
  });

  return (
    <div style={{
      background: C.bg, padding: '10px 10px 16px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
      color: C.navy,
    }}>
      <PackSummary data={data} engine={engine} ruleCount={rules.length} accentColor={cfg.accent} />

      <div style={{
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '.08em', color: C.muted, marginBottom: 8,
      }}>
        {cfg.label} — {rules.length} rule{rules.length !== 1 ? 's' : ''}
      </div>

      {rules.map((rule, i) => (
        <RuleCard key={rule.id || i} rule={rule} accentColor={cfg.accent} index={i} />
      ))}
    </div>
  );
};

export default PackDashboard;
